import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';

import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { SaveBar } from '@/components/ui/save-bar';
import { useBrief, useUpdateBriefSlice } from '@/lib/brief';

export const Route = createFileRoute('/_app/brief/faq')({
  staticData: { crumb: 'FAQ' },
  component: FaqPage,
});

const INTENTS = [
  ['general', 'Ogólne'],
  ['price', 'Cena'],
  ['safety', 'Bezpieczeństwo'],
  ['duration', 'Czas trwania'],
  ['eligibility', 'Dla kogo'],
  ['aftercare', 'Po zabiegu'],
  ['logistics', 'Logistyka'],
] as const;

interface FaqItem {
  q: string;
  a: string;
  intent?: string;
}

function FaqPage() {
  const { data: brief } = useBrief();
  const update = useUpdateBriefSlice();
  const [items, setItems] = React.useState<FaqItem[]>(() => (brief?.faq ?? []) as FaqItem[]);
  const [dirty, setDirty] = React.useState(false);
  const [savedStatus, setSavedStatus] = React.useState<'idle' | 'saved' | 'error'>('idle');

  React.useEffect(() => {
    if (!dirty) setItems((brief?.faq ?? []) as FaqItem[]);
  }, [brief?.faq, dirty]);

  const updateItem = (idx: number, patch: Partial<FaqItem>) => {
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
    setDirty(true);
  };
  const addItem = () => {
    setItems((prev) => [...prev, { q: '', a: '', intent: 'general' }]);
    setDirty(true);
  };
  const removeItem = (idx: number) => {
    setItems((prev) => prev.filter((_, i) => i !== idx));
    setDirty(true);
  };

  const onSave = async () => {
    setSavedStatus('idle');
    try {
      const cleaned = items.filter((i) => i.q.trim().length >= 5 && i.a.trim().length >= 5);
      await update.mutateAsync({ slice: 'faq', data: cleaned });
      setSavedStatus('saved');
      setDirty(false);
    } catch {
      setSavedStatus('error');
    }
  };

  const tooFewWarning = items.length < 3;

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSave();
      }}
      className="space-y-6"
    >
      <header>
        <h1 className="font-display text-2xl font-semibold tracking-tight text-text">FAQ</h1>
        <p className="mt-1 text-sm text-text-muted">
          Najczęstsze pytania, które dostajecie od pacjentów. Wpływają na sekcję „Częste pytania"
          na stronie publicznej. Wymagamy minimum 3 wpisów.
        </p>
      </header>

      <ul role="list" className="space-y-4">
        {items.map((item, idx) => (
          <li
            key={idx}
            className="rounded-[var(--radius-card)] bg-bg p-5 ring-1 ring-black/5"
          >
            <div className="flex items-start justify-between gap-3">
              <span className="font-mono text-xs text-text-subtle">#{idx + 1}</span>
              <button
                type="button"
                aria-label="Usuń wpis"
                onClick={() => removeItem(idx)}
                className="p-1 text-text-subtle hover:text-error"
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </button>
            </div>
            <div className="mt-3 grid gap-3">
              <Field label="Pytanie" htmlFor={`q-${idx}`} required>
                <Input
                  id={`q-${idx}`}
                  value={item.q}
                  onChange={(e) => updateItem(idx, { q: e.target.value })}
                />
              </Field>
              <Field label="Odpowiedź" htmlFor={`a-${idx}`} required>
                <Textarea
                  id={`a-${idx}`}
                  rows={3}
                  value={item.a}
                  onChange={(e) => updateItem(idx, { a: e.target.value })}
                />
              </Field>
              <Field label="Intencja" htmlFor={`intent-${idx}`} hint="grupa pytania na publicznej stronie">
                <Select
                  id={`intent-${idx}`}
                  value={item.intent ?? 'general'}
                  onChange={(e) => updateItem(idx, { intent: e.target.value })}
                >
                  {INTENTS.map(([v, label]) => (
                    <option key={v} value={v}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </li>
        ))}
      </ul>

      <Button type="button" variant="secondary" onClick={addItem}>
        <Plus className="size-4" aria-hidden="true" /> Dodaj pytanie
      </Button>

      {tooFewWarning ? (
        <p className="rounded-md bg-warning/10 p-3 text-sm text-warning ring-1 ring-warning/20">
          Min. 3 wpisy FAQ wymagane, aby sekcja pokazała się na publicznej stronie.
        </p>
      ) : null}

      <SaveBar
        saving={update.isPending}
        dirty={dirty}
        status={savedStatus}
        onCancel={() => {
          setItems((brief?.faq ?? []) as FaqItem[]);
          setDirty(false);
        }}
      />
    </form>
  );
}
